import { useCallback, useEffect, useState } from 'react';
import { PrefSegmentedControl, PrefSwitch } from '../components/PrefControls';
import {
  EDITOR_AUTOSAVE_ENABLED_KEY,
  EDITOR_AUTOSAVE_INTERVAL_KEY,
  EDITOR_AUTOSAVE_INTERVAL_OPTIONS,
  EDITOR_AUTOSAVE_PREFS_CHANGED,
  readEditorAutoSaveEnabled,
  readEditorAutoSaveIntervalSeconds,
  writeEditorAutoSaveEnabled,
  writeEditorAutoSaveIntervalSeconds,
  type EditorAutoSaveIntervalSeconds
} from './editorAutoSavePrefs';
import '../theme/themePreferences.css';

const INTERVAL_ITEMS = EDITOR_AUTOSAVE_INTERVAL_OPTIONS.map((seconds) => ({
  text: `${seconds} seconds`,
  value: seconds
}));

export function EditorAutoSavePreferencesPanel() {
  const [enabled, setEnabled] = useState(() => readEditorAutoSaveEnabled());
  const [intervalSeconds, setIntervalSeconds] = useState(() => readEditorAutoSaveIntervalSeconds());

  const syncFromStorage = useCallback(() => {
    setEnabled(readEditorAutoSaveEnabled());
    setIntervalSeconds(readEditorAutoSaveIntervalSeconds());
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === EDITOR_AUTOSAVE_ENABLED_KEY || e.key === EDITOR_AUTOSAVE_INTERVAL_KEY) {
        syncFromStorage();
      }
    };
    const onCustom = () => syncFromStorage();
    window.addEventListener('storage', onStorage);
    window.addEventListener(EDITOR_AUTOSAVE_PREFS_CHANGED, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(EDITOR_AUTOSAVE_PREFS_CHANGED, onCustom);
    };
  }, [syncFromStorage]);

  const onEnabledChanged = useCallback((next: boolean) => {
    setEnabled(next);
    writeEditorAutoSaveEnabled(next);
  }, []);

  const onIntervalChanged = useCallback((next: EditorAutoSaveIntervalSeconds) => {
    setIntervalSeconds(next);
    writeEditorAutoSaveIntervalSeconds(next);
  }, []);

  return (
    <section
      className="theme-preferences theme-preferences--first"
      aria-labelledby="editor-idle-timeout-preferences-heading"
    >
      <h3 id="editor-idle-timeout-preferences-heading" className="theme-preferences__title">
        Idle timeout
      </h3>
      <p className="theme-preferences__hint">
        Applies to the spreadsheet and Word editors. While you keep editing, nothing is saved
        automatically. After you pause for the selected time, changes are saved and the edit lock is
        released so others can edit.
      </p>
      <div className="theme-preferences__section">
        <div className="theme-preferences__compact-row">
          <PrefSwitch checked={enabled} onChange={onEnabledChanged} aria-label="Enable idle timeout" />
          <span className="theme-preferences__hint theme-preferences__hint--inline">
            Enable idle timeout
          </span>
        </div>
      </div>
      <div className="theme-preferences__section">
        <span className="theme-preferences__label">Save and release after inactivity</span>
        <div className={enabled ? undefined : 'pref-segmented--disabled'}>
          <PrefSegmentedControl
            className="theme-preferences__family-group"
            items={INTERVAL_ITEMS}
            value={intervalSeconds}
            aria-label="Idle timeout interval"
            onChange={(next) => {
              if (!enabled) return;
              onIntervalChanged(next);
            }}
          />
        </div>
        <p className="theme-preferences__hint theme-preferences__hint--tight">
          You can also change these from the File tab while editing a document. Use Stop Editing to
          save and unlock immediately.
        </p>
      </div>
    </section>
  );
}
